
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { SupplierModel } from '@/models/supplier';
import { ReportModel } from '@/models/report';
import { EventBus } from '@/main';
import { contextSyncService } from '@/services/contextSync';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import { ParamList } from '@/collections/params';
import { ParamModel } from '@/models/param';
import { ProductSegmentModel } from '@/models/product_segment';
import { PurchaseOrderModel, IPurchaseOrderItem } from '@/models/purchase_order';
import DeliveryLocationDialog, { LocationSelection } from './DeliveryLocationDialog.vue';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';

interface Product {
  variant_id: string;
  productName: string;
  variantName: string;
  sku: string;
  image?: string;
  currentInventory: number;
  reorderQuantity: number;
  reorderDate: string;
  inventoryReplenishment: number;
  coverageDateRange: string;
  coverageDays: number;
  supplier_id: string;
  supplier: string;
  unitCost?: number; // Unit cost/price for the product
  supplierSku?: string; // Supplier's SKU if available
  // Fields for agent context sync (explain_recommendation)
  avgDailySales?: number; // Historical sales velocity - units sold per day
  forecastDailySales?: number; // Forecast sales velocity - units sold per day (trend-adjusted)
  vendorLeadTime?: number; // Supplier lead time in days
  stockDays?: number; // Days of stock remaining at current velocity
  incomingInventory?: number; // Incoming/on-order inventory
  reorderCycle?: number; // Target coverage days (reorder cycle)
  safetyStock?: number; // Safety stock level
  // NEW: Additional fields for smarter recommendations
  inventoryRemainingDays?: number; // Days until stockout
  forecastLeadTimeDemand?: number; // Units needed during lead time
  reorderPoint?: number; // When to reorder
  forecastNextCycleDemand?: number; // Demand for next cycle
  forecastDailySalesCoverage?: number; // Daily sales rate for coverage period
  forecastInventoryNeed?: number; // Total inventory needed
  coverageStartDate?: string; // When coverage starts
  coverageEndDate?: string; // When coverage ends
  caseQuantity?: number; // Case pack quantity
  minOrderQuantity?: number; // Minimum order quantity
  maxOrderQuantity?: number; // Maximum order quantity
  minInventoryQuantity?: number; // Minimum inventory level
  maxInventoryQuantity?: number; // Maximum inventory level
  minStockCoverDays?: number; // Minimum stock cover days
  maxStockCoverDays?: number; // Maximum stock cover days
  '[case_quantity]'?: number; // Alternative case quantity field
  variantMinOrderQuantity?: number; // Alternative min order quantity field
  variantMaxOrderQuantity?: number; // Alternative max order quantity field
  // Filter-related fields for dynamic columns
  soldQuantity?: number; // Total units sold in period
  grossProfit?: number; // Gross profit amount
  profitMargin?: number; // Profit margin %
  productGrade?: string; // ABC classification (A, B, C)
}

interface PageOptions {
  page: number;
  itemsPerPage: number;
  sortBy?: string[];
  sortDesc?: boolean[];
}

/** Performance filter configuration from agent */
interface PerformanceFilter {
  type: string;
  topn?: number | null;
  description?: string;
  sortBy?: string | null;
  sortDesc?: boolean | null;
  additionalFilters?: Array<{
    code: string;
    operator: string;
    value: (string | number)[];
    dataType?: string;
  }>;
}

/** Parameters passed from the PO Agent action */
interface BuyingTableActionParameters {
  supplier_id?: string;
  supplier_name?: string;
  location?: string;
  date_range?: {
    start: string;
    end: string;
  };
  date_range_description?: string;
  performance_filter?: PerformanceFilter;
}

/** Chat response structure from the agent (passed but not used in this component) */
interface BuyingTableChatResponse {
  actions?: Array<{ action: string; parameters?: Record<string, unknown> }>;
  messages?: Array<{ content: string }>;
  current_agent?: string;
}

@Component({
  name: 'BuyingTableDisplay',
  components: {
    DeliveryLocationDialog
  }
})
export default class BuyingTableDisplay extends Vue {
  @Prop({ default: null }) readonly chatResponse!: BuyingTableChatResponse | null;
  @Prop({ default: () => ({}) }) readonly actionParameters!: BuyingTableActionParameters;

  /** Watch for date_range changes in actionParameters and update accordingly */
  @Watch('actionParameters.date_range', { deep: true })
  onDateRangeChange(newDateRange: { start: string; end: string } | undefined) {
    if (newDateRange && newDateRange.start && newDateRange.end) {
      this.currentDateRange = newDateRange;
      // Reset to first page and re-fetch with new date range
      this.pageOptions.page = 1;
      this.fetchProducts();
    }
  }

  /** Watch for performance_filter changes in actionParameters */
  @Watch('actionParameters.performance_filter', { deep: true, immediate: true })
  onPerformanceFilterChange(filter: PerformanceFilter | undefined) {
    if (filter?.type && filter.type !== 'none') {
      // Agent-provided filter takes precedence, clear local filter
      this.localPerformanceFilter = null;
      this.activeFilter = {
        type: filter.type,
        description: filter.description || filter.type.replace(/_/g, ' ')
      };
      // Reset to first page and re-fetch with filter
      this.pageOptions.page = 1;
      this.fetchProducts();
      this.cacheFilterContext();
    } else {
      // No valid filter - clear and fetch all products
      // Handles: undefined, null, empty object {}, or type === 'none'
      this.activeFilter = null;
      this.localPerformanceFilter = null;
      this.pageOptions.page = 1;
      this.fetchProducts();
      this.cacheFilterContext();
    }
  }

  // Expose CurrencyHelper to template
  private CurrencyHelper = CurrencyHelper;

  private products: Product[] = [];
  private selectedProducts: Product[] = [];
  private loading = true; // Start as true to show skeleton immediately on initial render
  private initialLoading = true;
  private creating = false;
  private reviewing = false;
  private explaining = false;
  private totalItems = 0;
  private fetchRequestId = 0; // Track latest request to ignore stale responses
  private countRequestId = 0; // Separate counter for count requests (allows parallel fetching)
  private showLocationDialog = false; // For delivery location selection

  private pageOptions: PageOptions = {
    page: 1,
    itemsPerPage: 25,
    sortBy: [],
    sortDesc: []
  };

  /** Track product variant IDs whose images failed to load */
  private failedImages: Set<string> = new Set();

  /** Current date range for filtering suggestions (last 3 months by default) */
  private currentDateRange: { start: string; end: string } = this.getDefaultDateRange();

  /** Active performance filter (top sellers, ABC class, etc.) */
  private activeFilter: { type: string; description: string } | null = null;

  /** Locally-applied performance filter (for direct UI application, bypasses agent) */
  private localPerformanceFilter: PerformanceFilter | null = null;

  /** Dialog state for supplier selection when multiple suppliers detected */
  private showSupplierSelectionDialog: boolean = false;
  private detectedSuppliers: Array<{ id: number; name: string; itemCount: number }> = [];
  private selectedSupplierForPO: { id: number; name: string } | null = null;

  /** Supplier filter for the buying table header dropdown */
  private selectedSupplierFilter: { id: number; name: string } | null = null;

  /** Location filter IDs - supports multiple selection */
  private selectedLocationIds: Array<number | string> = ['all-location-option'];

  /** Available locations loaded from API */
  private availableLocations: StoreLocationModel[] = [];

  /** Loading state for locations */
  private loadingLocations: boolean = false;

  /** Filter categories for the filter menu */
  private filterCategories = [
    {
      name: 'Sales Performance',
      icon: 'mdi-chart-line',
      color: 'blue',
      filters: [
        { type: 'top_sellers', label: 'Top Sellers', description: 'Products with highest units sold' },
        { type: 'slow_movers', label: 'Slow Movers', description: 'Products with lowest units sold' },
        { type: 'trending_up', label: 'Trending Up', description: 'Products with increasing sales' },
        { type: 'trending_down', label: 'Trending Down', description: 'Products with declining sales' }
      ]
    },
    {
      name: 'Profitability',
      icon: 'mdi-currency-usd',
      color: 'green',
      filters: [
        { type: 'highest_profit', label: 'Highest Profit', description: 'Products with highest profit amount' },
        { type: 'high_margin', label: 'High Margin', description: 'Products with highest profit margin %' },
        { type: 'low_margin', label: 'Low Margin', description: 'Products with lowest profit margin %' }
      ]
    },
    {
      name: 'Inventory Urgency',
      icon: 'mdi-alert-circle',
      color: 'orange',
      filters: [
        { type: 'critical_stock', label: 'Critical Stock', description: 'Items with ≤7 days of stock' },
        { type: 'stockout_risk', label: 'Stockout Risk', description: 'Items at risk of stockout' },
        { type: 'reorder_now', label: 'Reorder Now', description: 'Items that need immediate reorder' }
      ]
    },
    {
      name: 'ABC Classification',
      icon: 'mdi-sort-alphabetical-ascending',
      color: 'purple',
      filters: [
        { type: 'class_a', label: 'A-Class', description: 'Top priority products (80% revenue)' },
        { type: 'class_b', label: 'B-Class', description: 'Medium priority products (15% revenue)' },
        { type: 'class_c', label: 'C-Class', description: 'Low priority products (5% revenue)' }
      ]
    }
  ];

  /** Base table headers (always shown) */
  private baseHeaders = [
    { text: 'Product', value: 'productInfo', sortable: false, width: '300px' },
    { text: 'Current Stock', value: 'currentInventory', align: 'center', sortable: false, width: '100px' },
  ];

  /** Trailing table headers (always shown at end) */
  private trailingHeaders = [
    { text: 'Reorder Date', value: 'reorderDate', sortable: false, width: '180px' },
    {
      text: 'Suggested',
      value: 'inventoryReplenishment',
      align: 'center',
      sortable: false,
      width: '100px'
    },
    {
      text: 'Order Qty',
      value: 'reorderQuantity',
      align: 'center',
      sortable: false,
      width: '130px'
    }
  ];

  /** Filter type to dynamic column mapping (includes sort direction) */
  private filterColumnMapping: Record<string, { text: string; value: string; align: string; width: string; sortDesc: boolean }> = {
    // Sales filters (top = DESC, bottom = ASC)
    top_sellers: { text: 'Units Sold', value: 'soldQuantity', align: 'center', width: '100px', sortDesc: true },
    slow_movers: { text: 'Units Sold', value: 'soldQuantity', align: 'center', width: '100px', sortDesc: false },
    trending_up: { text: 'Avg Daily', value: 'avgDailySales', align: 'center', width: '100px', sortDesc: true },
    trending_down: { text: 'Avg Daily', value: 'avgDailySales', align: 'center', width: '100px', sortDesc: false },
    // Profitability filters
    highest_profit: { text: 'Profit', value: 'grossProfit', align: 'center', width: '100px', sortDesc: true },
    high_margin: { text: 'Margin %', value: 'profitMargin', align: 'center', width: '100px', sortDesc: true },
    low_margin: { text: 'Margin %', value: 'profitMargin', align: 'center', width: '100px', sortDesc: false },
    // Urgency filters (low days = urgent, so ASC)
    critical_stock: { text: 'Stock Days', value: 'stockDays', align: 'center', width: '100px', sortDesc: false },
    stockout_risk: { text: 'Stock Days', value: 'stockDays', align: 'center', width: '100px', sortDesc: false },
    reorder_now: { text: 'Stock Days', value: 'stockDays', align: 'center', width: '100px', sortDesc: false },
    // ABC filters (no sort, just filter)
    class_a: { text: 'Grade', value: 'productGrade', align: 'center', width: '80px', sortDesc: true },
    class_b: { text: 'Grade', value: 'productGrade', align: 'center', width: '80px', sortDesc: true },
    class_c: { text: 'Grade', value: 'productGrade', align: 'center', width: '80px', sortDesc: true },
  };

  /** Filter configurations for direct UI application (mirrors backend FILTER_CONFIGS) */
  private readonly FILTER_CONFIGS: Record<string, {
    sortBy: string | null;
    sortDesc: boolean;
    description: string;
    useTopn: boolean;
    defaultTopn?: number;
    defaultThreshold?: number;
    thresholdFilter?: boolean;
    additionalFilters?: Array<{ code: string; operator: string; value: string[]; dataType: string }>;
  }> = {
    // Sales filters
    top_sellers: {
      sortBy: '[sold_quantity_param]',
      sortDesc: true,
      description: 'Top 20 sellers',
      useTopn: true,
      defaultTopn: 20,
    },
    slow_movers: {
      sortBy: '[sold_quantity_param]',
      sortDesc: false,
      description: 'Bottom 20 sellers',
      useTopn: true,
      defaultTopn: 20,
    },
    trending_up: {
      sortBy: '[average_daily_units_sold_param]',
      sortDesc: true,
      description: 'Top 20 trending up',
      useTopn: true,
      defaultTopn: 20,
    },
    trending_down: {
      sortBy: '[average_daily_units_sold_param]',
      sortDesc: false,
      description: 'Bottom 20 trending down',
      useTopn: true,
      defaultTopn: 20,
    },
    // Profitability filters
    highest_profit: {
      sortBy: '[gross_profit]',
      sortDesc: true,
      description: 'Top 20 by profit',
      useTopn: true,
      defaultTopn: 20,
    },
    high_margin: {
      sortBy: '[product_profit_margin]',
      sortDesc: true,
      description: 'Top 20 by margin',
      useTopn: true,
      defaultTopn: 20,
    },
    low_margin: {
      sortBy: '[product_profit_margin]',
      sortDesc: false,
      description: 'Bottom 20 by margin',
      useTopn: true,
      defaultTopn: 20,
    },
    // Urgency filters (threshold-based)
    critical_stock: {
      sortBy: '[stock_days_inventory_planning]',
      sortDesc: false,
      description: 'Critical stock (≤7 days)',
      useTopn: false,
      thresholdFilter: true,
      defaultThreshold: 7,
      additionalFilters: [
        { code: '[stock_days_inventory_planning]', operator: 'lte', value: ['7'], dataType: 'number' }
      ],
    },
    stockout_risk: {
      sortBy: '[stock_days_inventory_planning]',
      sortDesc: false,
      description: 'Stockout risk (≤14 days)',
      useTopn: false,
      thresholdFilter: true,
      defaultThreshold: 14,
      additionalFilters: [
        { code: '[stock_days_inventory_planning]', operator: 'lte', value: ['14'], dataType: 'number' }
      ],
    },
    reorder_now: {
      sortBy: '[reorder_date]',
      sortDesc: false,
      description: 'Need to reorder now',
      useTopn: false,
    },
    // ABC Classification filters
    class_a: {
      sortBy: null,
      sortDesc: true,
      description: 'A-class products',
      useTopn: false,
      additionalFilters: [
        { code: '[product_grade]', operator: 'eq', value: ['A'], dataType: 'string' }
      ],
    },
    class_b: {
      sortBy: null,
      sortDesc: true,
      description: 'B-class products',
      useTopn: false,
      additionalFilters: [
        { code: '[product_grade]', operator: 'eq', value: ['B'], dataType: 'string' }
      ],
    },
    class_c: {
      sortBy: null,
      sortDesc: true,
      description: 'C-class products',
      useTopn: false,
      additionalFilters: [
        { code: '[product_grade]', operator: 'eq', value: ['C'], dataType: 'string' }
      ],
    },
  };

  /** Get the current sort column value (for highlighting in header) */
  get currentSortColumn(): string | null {
    if (!this.activeFilter?.type) return null;
    const mapping = this.filterColumnMapping[this.activeFilter.type];
    return mapping?.value || null;
  }

  /** Get the current sort direction (true = DESC, false = ASC) */
  get currentSortDesc(): boolean {
    if (!this.activeFilter?.type) return true;
    const mapping = this.filterColumnMapping[this.activeFilter.type];
    return mapping?.sortDesc ?? true;
  }

  /** Computed table headers with dynamic column based on active filter */
  get tableHeaders() {
    const headers = [...this.baseHeaders];

    // Add dynamic column if a filter is active
    if (this.activeFilter?.type) {
      const dynamicColumn = this.filterColumnMapping[this.activeFilter.type];
      if (dynamicColumn) {
        headers.push({ ...dynamicColumn, sortable: false });
      }
    }

    // Add trailing headers
    headers.push(...this.trailingHeaders);

    return headers;
  }

  get supplierId(): string {
    return this.actionParameters?.supplier_id || '';
  }

  get supplierName(): string {
    return this.actionParameters?.supplier_name || 'Unknown Supplier';
  }

  /** Display name for the supplier filter chip */
  get supplierDisplayName(): string {
    if (this.selectedSupplierFilter) {
      return this.selectedSupplierFilter.name;
    }
    // If a specific supplier was passed via actionParameters, show that
    if (this.supplierId) {
      return this.supplierName;
    }
    return 'All';
  }

  /** Available suppliers extracted from products for the filter dropdown */
  get availableSuppliers(): Array<{ id: number; name: string }> {
    const supplierMap = new Map<number, string>();
    for (const product of this.products) {
      const supplierId = parseInt(product.supplier_id, 10);
      if (supplierId && supplierId > 0 && product.supplier) {
        supplierMap.set(supplierId, product.supplier);
      }
    }
    return Array.from(supplierMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /** Options for location filter dropdown with "All Locations" option */
  get locationFilterOptions(): Array<{ id: number | string; name: string; disabled?: boolean }> {
    const isAllSelected = this.selectedLocationIds.includes('all-location-option');
    return [
      { id: 'all-location-option', name: 'All Locations (Show All)' },
      ...this.availableLocations.map(loc => ({
        id: loc.id,
        name: loc.name,
        disabled: isAllSelected // Disable individual locations when "All" is selected
      }))
    ];
  }

  get location(): string {
    return this.actionParameters?.location || 'all';
  }

  /** User-facing display string for location */
  get locationDisplay(): string {
    const loc = this.location;
    if (!loc || loc === 'all') {
      return 'All Locations';
    }
    return loc;
  }

  /** Display name for the location filter chip */
  get locationFilterDisplayName(): string {
    // If "All Locations" is selected
    if (this.selectedLocationIds.includes('all-location-option')) {
      return 'All';
    }
    // Filter out the "all-location-option" and count actual selections
    const actualSelections = this.selectedLocationIds.filter(id => id !== 'all-location-option');
    if (actualSelections.length > 0) {
      return `Selected (${actualSelections.length})`;
    }
    return 'All';
  }

  /** User-facing display string for date range */
  get dateRangeDisplay(): string {
    if (!this.currentDateRange.start || !this.currentDateRange.end) {
      return 'Last 3 months';
    }
    const startDate = new Date(this.currentDateRange.start);
    const endDate = new Date(this.currentDateRange.end);
    const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const start = startDate.toLocaleDateString('en-US', formatOptions);
    const end = endDate.toLocaleDateString('en-US', formatOptions);
    return `${start} - ${end}`;
  }

  /** Total units across all selected products */
  get totalUnits(): number {
    return this.selectedProducts.reduce((sum, p) => {
      const qty = this.parseNumber(p.reorderQuantity);
      return sum + qty;
    }, 0);
  }

  /** Formatted total units with thousands separator */
  get totalUnitsFormatted(): string {
    return this.totalUnits.toLocaleString();
  }

  /** Total cost across all selected products (qty × unit cost) */
  get totalCost(): number {
    return this.selectedProducts.reduce((sum, p) => {
      const qty = this.parseNumber(p.reorderQuantity);
      const price = this.parseNumber(p.unitCost);
      return sum + (qty * price);
    }, 0);
  }

  /** Formatted total cost with currency format */
  get totalCostFormatted(): string {
    return this.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** Average coverage days across selected products */
  get estimatedCoverageDays(): number {
    if (this.selectedProducts.length === 0) return 0;
    const totalCoverage = this.selectedProducts.reduce((sum, p) => {
      const days = this.parseNumber(p.coverageDays);
      return sum + days;
    }, 0);
    return Math.round(totalCoverage / this.selectedProducts.length);
  }

  /**
   * Safely parse a value as a number
   * @param value - Raw value that might be string or number
   * @param defaultValue - Default value if parsing fails
   * @returns Parsed number
   */
  private parseNumber(value: any, defaultValue = 0): number {
    if (typeof value === 'number' && !isNaN(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  }

  /** Get default date range (last 3 months) */
  private getDefaultDateRange(): { start: string; end: string } {
    const formatDate = (date: Date) => date.toISOString().slice(0, 10);
    const now = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(now.getMonth() - 3);
    return {
      start: formatDate(threeMonthsAgo),
      end: formatDate(now)
    };
  }

  /** Send message to chatbox asking agent to change date range */
  private requestDateRangeChange() {
    EventBus.$emit('chatbox:send-message', {
      message: 'I want to change the date range for the buying table suggestions',
      metadata: {
        action: 'change_date_range',
        context: {
          current_date_range: this.currentDateRange,
          supplier_id: this.supplierId,
          supplier_name: this.supplierName
        }
      }
    });
  }

  /** Update date range from agent response and refresh data */
  private updateDateRange(dateRange: { start: string; end: string }) {
    this.currentDateRange = dateRange;
    // Refresh products with new date range
    this.fetchProducts();
  }

  /** Clear performance filter and refresh data */
  private clearFilter() {
    this.activeFilter = null;
    this.localPerformanceFilter = null;
    // Reset to first page and refresh data without filter
    this.pageOptions.page = 1;
    this.fetchProducts();
    this.cacheFilterContext();
  }

  /**
   * Cache current performance filter locally for chat context.
   * Filter will be attached to chat metadata when user sends a message.
   */
  private cacheFilterContext(): void {
    const performanceFilter = this.localPerformanceFilter || this.actionParameters?.performance_filter;

    if (performanceFilter && performanceFilter.type !== 'none') {
      contextSyncService.setLocalFilterContext('buying_table', {
        type: performanceFilter.type,
        description: performanceFilter.description,
        threshold: this.extractThreshold(performanceFilter),
        topn: performanceFilter.topn,
      });
    } else {
      contextSyncService.clearLocalFilterContext();
    }
  }

  /**
   * Extract threshold value from performance filter.
   * Used for critical_stock and stockout_risk filters.
   */
  private extractThreshold(filter: PerformanceFilter): number | undefined {
    if (filter.type === 'critical_stock' || filter.type === 'stockout_risk') {
      const thresholdFilter = filter.additionalFilters?.find(
        (f) => f.code === '[stock_days_inventory_planning]'
      );
      if (thresholdFilter) {
        return parseInt(String(thresholdFilter.value[0]), 10);
      }
      // Return default thresholds if not found in additionalFilters
      return filter.type === 'critical_stock' ? 7 : 14;
    }
    return undefined;
  }

  /** Filter by supplier from the header dropdown */
  private filterBySupplier(supplier: { id: number; name: string } | null) {
    this.selectedSupplierFilter = supplier;
    // Reset page and clear selection when filter changes
    this.pageOptions.page = 1;
    this.selectedProducts = [];
    this.fetchProducts();
    // Update screen context with new supplier for agent context
    this.syncScreenContext();
  }

  /** Fetch available store locations for the filter dropdown */
  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      const storeLocationList = new StoreLocationList();
      await storeLocationList.fetch();
      this.availableLocations = storeLocationList.items;
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      this.loadingLocations = false;
    }
  }

  /** Toggle "All Locations" selection */
  private toggleAllLocations() {
    if (this.selectedLocationIds.includes('all-location-option')) {
      // Deselecting "All Locations" - clear to empty (will show all)
      this.selectedLocationIds = [];
    } else {
      // Selecting "All Locations" - clear other selections
      this.selectedLocationIds = ['all-location-option'];
    }
  }

  /** Toggle a specific location selection */
  private toggleLocation(locationId: number) {
    const index = this.selectedLocationIds.indexOf(locationId);
    if (index > -1) {
      // Remove from selection
      this.selectedLocationIds = this.selectedLocationIds.filter(id => id !== locationId);
    } else {
      // Add to selection (and remove 'all-location-option' if present)
      this.selectedLocationIds = [
        ...this.selectedLocationIds.filter(id => id !== 'all-location-option'),
        locationId
      ];
    }
  }

  /** Apply location filter and refresh data */
  private applyLocationFilter() {
    // Reset page and clear selection when filter changes
    this.pageOptions.page = 1;
    this.selectedProducts = [];
    this.fetchProducts();
    // Close the v-menu dropdown
    if (this.$refs.locationMenu) {
      (this.$refs.locationMenu as any).isActive = false;
    }
  }

  /** Apply a performance filter from the filter menu (direct, no agent round-trip) */
  private applyFilter(filterType: string) {
    const config = this.FILTER_CONFIGS[filterType];
    if (!config) return;

    // Build performance_filter object locally (same structure as agent returns)
    const performanceFilter: PerformanceFilter = {
      type: filterType,
      description: config.description,
    };

    // Add sort params if specified
    if (config.sortBy) {
      performanceFilter.sortBy = config.sortBy;
      performanceFilter.sortDesc = config.sortDesc;
    }

    // Add topn if this filter uses it
    if (config.useTopn && config.defaultTopn) {
      performanceFilter.topn = config.defaultTopn;
    }

    // Add additional filters if specified
    if (config.additionalFilters) {
      performanceFilter.additionalFilters = config.additionalFilters;
    }

    // Apply filter directly (bypass agent)
    this.localPerformanceFilter = performanceFilter;
    this.activeFilter = {
      type: filterType,
      description: config.description
    };
    this.pageOptions.page = 1;
    this.fetchProducts();
    this.cacheFilterContext();
  }

  /** Handle agent response with date range metadata */
  private handleDateRangeResponse(response: { metadata?: { date_range?: { start: string; end: string } } }) {
    if (response?.metadata?.date_range) {
      this.updateDateRange(response.metadata.date_range);
    }
  }

  async mounted() {
    // Listen for date range changes from agent (EventBus fallback)
    EventBus.$on('buying-table:update-date-range', this.handleDateRangeResponse);

    // Initialize date range from props if provided
    if (this.actionParameters?.date_range?.start && this.actionParameters?.date_range?.end) {
      this.currentDateRange = this.actionParameters.date_range;
    }

    // Fetch available locations for the filter dropdown
    this.fetchLocations();

    // Note: fetchProducts() is called by the performance_filter watcher with immediate: true,
    // so we don't need to call it here. This avoids duplicate requests on initial mount.
  }

  beforeDestroy() {
    // Clean up event listener
    EventBus.$off('buying-table:update-date-range', this.handleDateRangeResponse);
  }

  private async fetchProducts() {
    // Determine supplier ID - prioritize UI filter, then actionParameters
    let effectiveSupplierId: number;
    if (this.selectedSupplierFilter) {
      effectiveSupplierId = this.selectedSupplierFilter.id;
    } else {
      const parsedSupplierId = this.supplierId ? parseInt(this.supplierId, 10) : NaN;
      effectiveSupplierId = Number.isFinite(parsedSupplierId) && parsedSupplierId > 0 ? parsedSupplierId : 0;
    }
    const hasValidSupplier = effectiveSupplierId > 0;

    // Determine effective location IDs - filter out "all-location-option"
    const actualLocationIds = this.selectedLocationIds.filter(id => id !== 'all-location-option');

    // Increment request ID and capture it for this request
    // This allows us to ignore stale responses if a newer request was made
    const currentRequestId = ++this.fetchRequestId;

    this.loading = true;

    // Only show initial loading on first fetch
    if (this.products.length === 0) {
      this.initialLoading = true;
    }

    try {
      // Create supplier filter for the specific supplier
      const filters = new FilterColumnList();

      // Add location filter if specific locations are selected
      if (actualLocationIds.length > 0) {
        const locationFilter = new FilterColumnModel();
        locationFilter.mapData({
          code: 'location_id',
          name: 'Location',
          value: actualLocationIds,
          operator: 'eq',
          dataType: 'text',
        });
        filters.add(locationFilter);
      }

      // Create supplier model - use 0 as placeholder ID when fetching all suppliers
      const supplier = new SupplierModel({ id: effectiveSupplierId });

      // Define columns to fetch - includes additional fields for agent context
      const columns = [
        // Basic product info
        { code: 'variant_id', dataType: 'number', name: 'Variant ID' },
        { code: '[average_daily_units_sold_param]', dataType: 'number', name: 'Units Sold Per Day' },
        { code: '[average_daily_units_sold_forecast]', dataType: 'number', name: 'Forecast Units Sold Per Day' },
        { code: '[sold_quantity_param]', dataType: 'number', name: 'Units Sold' },
        { code: '[stock_days_inventory_planning]', dataType: 'number', name: 'Inventory Remaining Days' },
        { code: '[inventory_replenishment]', dataType: 'number', name: 'Reorder Quantity' },
        { code: '[reorder_date]', dataType: 'number', name: 'Reorder Date' },
        { code: 'product_barcode', dataType: 'string', name: 'Barcode' },
        { code: '[incoming_inventory_quantity]', dataType: 'number', name: 'Incoming Inventory' },
        { code: '[average_unit_cost]', dataType: 'number', name: 'Unit Cost' },
        // Agent context fields
        { code: '[vendor_lead_time]', dataType: 'number', name: 'Vendor Lead Time' },
        { code: '[reorder_cycle]', dataType: 'number', name: 'Reorder Cycle' },
        { code: '[safety_stock_level]', dataType: 'number', name: 'Safety Stock' },
        // Coverage dates
        { code: '[coverage_start_date]', dataType: 'date', name: 'Coverage Start Date' },
        { code: '[coverage_end_date]', dataType: 'date', name: 'Coverage End Date' },
        // Filter-related columns (for dynamic display)
        { code: '[gross_profit]', dataType: 'currency', name: 'Gross Profit' },
        { code: '[product_profit_margin]', dataType: 'number', name: 'Margin %' },
        { code: '[product_grade]', dataType: 'string', name: 'ABC Grade' },
      ];

      // Use local filter if set, otherwise fall back to actionParameters
      const performanceFilter = this.localPerformanceFilter || this.actionParameters?.performance_filter;

      // Build report params, including topn if filter specifies it
      const reportParams = new ParamList();
      if (performanceFilter?.topn) {
        const topnParam = new ParamModel();
        topnParam.mapData({
          name: 'Show Top',
          code: 'topn',
          valueType: 'single',
          value: [String(performanceFilter.topn)],
          dataType: 'text',
        });
        reportParams.add(topnParam);
      }

      // Add additional filters from performance filter (e.g., ABC class, critical stock)
      if (performanceFilter?.additionalFilters) {
        for (const additionalFilter of performanceFilter.additionalFilters) {
          const filterModel = new FilterColumnModel();
          // Ensure values are strings (API expects string array)
          const values = additionalFilter.value.map((v: any) => String(v));
          filterModel.mapData({
            code: additionalFilter.code,
            operator: additionalFilter.operator,
            value: values,
            dataType: additionalFilter.dataType || 'string',
          });
          filters.add(filterModel);
        }
      }

      // Determine sort configuration - use filter's sortBy or default
      let sortBy: string[] = ['[reorder_date]', '[inventory_replenishment]'];
      let sortDesc: boolean[] = [false, true];
      if (performanceFilter?.sortBy) {
        sortBy = [performanceFilter.sortBy];
        sortDesc = [performanceFilter.sortDesc ?? true];
      }

      const reportOptions: any = {
        initialFilterColumns: filters,
        pageOptions: {
          page: String(this.pageOptions.page),
          itemsPerPage: String(this.pageOptions.itemsPerPage),
          sortBy,
          sortDesc,
          multiSort: false
        },
        suggestFutureReorderQuantity: true,
        initialReportParams: reportParams,
        productSegment: new ProductSegmentModel(),
        columns,  // Custom columns including forecast velocity and agent context fields
        dateRange: this.currentDateRange  // Filter suggestions based on current date range
      };

      // Get total count (non-blocking - let it update in the background)
      this.fetchProductsCount();

      // Fetch products - use 'eq' filter for specific supplier, 'all' for all suppliers
      const reportModel: ReportModel = await supplier.fetchProductsWithOptions(
        'inventory_replenishment',
        hasValidSupplier ? 'eq' : 'all', // 'all' skips supplier filter
        reportOptions
      );

      // Check if this response is stale (a newer request was made while waiting)
      if (currentRequestId !== this.fetchRequestId) {
        return; // Ignore stale response, newer request will handle UI update
      }

      const rawProducts = reportModel.getReportRowsData();

      // Transform products and hide loading in same sync block
      // This ensures Vue batches both updates in the same render cycle
      this.products = rawProducts.map((p: any) => this.transformRawProduct(p));
      this.loading = false;
      this.initialLoading = false; // Mark initial load complete after first successful fetch

      

      // Sync displayed products to agent context for accurate recommendations
      this.syncDisplayedProductsToAgent();

    } catch (error) {
      // Hide loading on error too
      this.loading = false;
      this.initialLoading = false;
      EventBus.$emit('show-snackbar', {
        message: 'Failed to load buying table data',
        color: 'error'
      });
    }
  }

  /**
   * Sync currently displayed products to the agent context.
   * This ensures the agent knows exactly what the user sees in the buying table
   * and can provide accurate explanations for specific products.
   */
  private syncDisplayedProductsToAgent() {
    if (this.products.length === 0) return;

    // Map to a normalized format the agent can understand
    const displayedProducts = this.products.map((p) => ({
      // Product identification
      variantId: p.variant_id,
      productName: p.productName,
      variantName: p.variantName,
      sku: p.sku,

      // Inventory metrics
      currentStock: p.currentInventory,
      suggestedQty: p.inventoryReplenishment,
      reorderQuantity: p.reorderQuantity,

      // Coverage and timing
      reorderDate: p.reorderDate,
      coverageDays: p.coverageDays,

      // Supplier info
      supplierId: this.supplierId,
      supplierName: this.supplierName,

      // Cost
      unitCost: p.unitCost || 0,

      // Additional fields for explain_recommendation
      avgDailySales: p.avgDailySales || 0,
      forecastDailySales: p.forecastDailySales || 0,
      vendorLeadTime: p.vendorLeadTime || 14,
      stockDays: p.stockDays || 0,
      incomingInventory: p.incomingInventory || 0,
      reorderCycle: p.reorderCycle || 0,
      safetyStock: p.safetyStock || 0,

      // NEW: Critical metrics for smarter recommendations
      // Urgency detection
      inventoryRemainingDays: p.inventoryRemainingDays || p.stockDays || 0,
      forecastLeadTimeDemand: p.forecastLeadTimeDemand || 0,
      reorderPoint: p.reorderPoint || 0,

      // Forecast calculations
      forecastNextCycleDemand: p.forecastNextCycleDemand || 0,
      forecastInventoryNeed: p.forecastInventoryNeed || 0,
      // Daily velocity for next/coverage cycle (used by AI explanation)
      forecastDailySalesCoverage: p.forecastDailySalesCoverage || 0,

      // Coverage dates
      coverageStartDate: p.coverageStartDate || null,
      coverageEndDate: p.coverageEndDate || null,

      // Order constraints
      caseQuantity: p['[case_quantity]'] || p.caseQuantity || 0,
      minOrderQuantity: p.variantMinOrderQuantity || 0,
      maxOrderQuantity: p.variantMaxOrderQuantity || 0,

      // Inventory constraints
      minInventoryQuantity: p.minInventoryQuantity || 0,
      maxInventoryQuantity: p.maxInventoryQuantity || 0,
      minStockCoverDays: p.minStockCoverDays || 0,
      maxStockCoverDays: p.maxStockCoverDays || 0
    }));

    // Emit event for POBuilder to include in next chat request
    EventBus.$emit('po-builder:products-loaded', {
      products: displayedProducts,
      supplierId: this.supplierId,
      supplierName: this.supplierName,
      totalCount: this.totalItems,
    });

    // Also sync directly to context API (if session is set)
    if (contextSyncService.getSessionId()) {
      contextSyncService.updateDataContext('reorder_suggestions', displayedProducts, {
        totalCount: this.totalItems,
      });

      // Sync screen context for automatic context attachment
      this.syncScreenContext();
    }
  }

  /**
   * Sync screen context to contextSyncService for automatic context attachment.
   * Called after data loads so chatbox can include screen info in messages.
   */
  private syncScreenContext(): void {
    if (!contextSyncService.getSessionId()) {
      return;
    }

    // Use effective supplier: from dropdown selection OR from actionParameters
    const effectiveSupplierId = this.selectedSupplierFilter?.id?.toString() || this.supplierId || '';
    const effectiveSupplierName = this.selectedSupplierFilter?.name || this.supplierName || 'All Suppliers';

    contextSyncService.updateScreenContext({
      screen_name: 'buying_table',
      screen_path: '/purchase-assistant',
      screen_params: {
        supplier_id: effectiveSupplierId,
        supplier_name: effectiveSupplierName,
        location: this.location,
      },
      component_state: {
        screen_type: 'buying_table',
        screen_data: {
          supplier_id: effectiveSupplierId,
          supplier_name: effectiveSupplierName,
          location: this.location,
          total_products: this.totalItems,
          displayed_count: this.products.length,
          selected_count: this.selectedProducts.length,
          page: this.pageOptions.page,
          items_per_page: this.pageOptions.itemsPerPage,
          // Include summary of displayed products (limit to avoid large payloads)
          products_summary: this.products.slice(0, 20).map(p => ({
            variant_id: p.variant_id,
            product_name: p.productName,
            sku: p.sku,
            current_inventory: p.currentInventory,
            suggested_qty: p.inventoryReplenishment,
            reorder_quantity: p.reorderQuantity,
            unit_cost: p.unitCost,
          })),
          // Selection summary
          selection_summary: this.selectedProducts.length > 0 ? {
            count: this.selectedProducts.length,
            total_units: this.totalUnits,
            total_cost: this.totalCost,
            estimated_coverage_days: this.estimatedCoverageDays,
          } : undefined,
        },
      },
    });
  }

  private async fetchProductsCount() {
    // Determine supplier ID - prioritize UI filter, then actionParameters (same as fetchProducts)
    let effectiveSupplierId: number;
    if (this.selectedSupplierFilter) {
      effectiveSupplierId = this.selectedSupplierFilter.id;
    } else {
      const parsedSupplierId = this.supplierId ? parseInt(this.supplierId, 10) : NaN;
      effectiveSupplierId = Number.isFinite(parsedSupplierId) && parsedSupplierId > 0 ? parsedSupplierId : 0;
    }
    const hasValidSupplier = effectiveSupplierId > 0;

    // Determine effective location IDs - filter out "all-location-option" (same as fetchProducts)
    const actualLocationIds = this.selectedLocationIds.filter(id => id !== 'all-location-option');

    // Track request to ignore stale responses (uses separate counter to allow parallel fetching)
    const currentCountRequestId = ++this.countRequestId;

    try {
      const filters = new FilterColumnList();

      // Add location filter if specific locations are selected (same as fetchProducts)
      if (actualLocationIds.length > 0) {
        const locationFilter = new FilterColumnModel();
        locationFilter.mapData({
          code: 'location_id',
          name: 'Location',
          value: actualLocationIds,
          operator: 'eq',
          dataType: 'text',
        });
        filters.add(locationFilter);
      }

      // Create supplier model - use 0 as placeholder ID when fetching all suppliers
      const supplier = new SupplierModel({ id: effectiveSupplierId });

      // Use local filter if set, otherwise fall back to actionParameters (same as fetchProducts)
      const performanceFilter = this.localPerformanceFilter || this.actionParameters?.performance_filter;

      // Add additional filters from performance filter (same as fetchProducts)
      if (performanceFilter?.additionalFilters) {
        for (const additionalFilter of performanceFilter.additionalFilters) {
          const filterModel = new FilterColumnModel();
          const values = additionalFilter.value.map((v: any) => String(v));
          filterModel.mapData({
            code: additionalFilter.code,
            operator: additionalFilter.operator,
            value: values,
            dataType: additionalFilter.dataType || 'string',
          });
          filters.add(filterModel);
        }
      }

      // Build report params, including topn if filter specifies it (same as fetchProducts)
      const reportParams = new ParamList();
      if (performanceFilter?.topn) {
        const topnParam = new ParamModel();
        topnParam.mapData({
          name: 'Show Top',
          code: 'topn',
          valueType: 'single',
          value: [String(performanceFilter.topn)],
          dataType: 'text',
        });
        reportParams.add(topnParam);
      }

      const countOptions: any = {
        initialFilterColumns: filters,
        suggestFutureReorderQuantity: true,
        initialReportParams: reportParams,
        productSegment: new ProductSegmentModel(),
        dateRange: this.currentDateRange  // Filter count based on current date range
      };

      const count = await supplier.countProductsWithOptions(
        'inventory_replenishment',
        hasValidSupplier ? 'eq' : 'all', // 'all' skips supplier filter
        countOptions
      );

      // Only update if this is still the latest request
      if (currentCountRequestId !== this.countRequestId) {
        return; // Ignore stale response
      }

      this.totalItems = Number(count.totalItems) || 0;
    } catch (error) {
      // Only log error if this is still the latest request
      if (currentCountRequestId === this.countRequestId) {
        console.error('[BuyingTableDisplay] Failed to fetch product count:', error);
      }
    }
  }

  /**
   * Transform raw product data from API into Product interface
   * @param product - Raw product data from report API
   * @returns Transformed Product object
   */
  private transformRawProduct(product: any): Product {
    // Extract date fields
    const reorderDate = product['[reorder_date]'];
    const coverageStartDate = product['[coverage_start_date]'];
    const coverageEndDate = product['[coverage_end_date]'];

    // Parse numeric values safely (API may return strings)
    const currentInventory = this.parseNumber(product['[Actual Inventory Quantity]']);
    const inventoryReplenishment = this.parseNumber(product['[inventory_replenishment]']);
    const reorderCycle = this.parseNumber(product['[reorder_cycle]']);
    const vendorLeadTime = this.parseNumber(product['[vendor_lead_time]'], 14);

    // Calculate coverage date range and days
    let coverageDateRange = 'N/A';
    let coverageDays = 0;

    if (coverageStartDate && coverageEndDate) {
      const startDate = new Date(coverageStartDate);
      const endDate = new Date(coverageEndDate);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const start = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const end = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        coverageDateRange = `${start} - ${end}`;
        coverageDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      }
    }

    // Fallback: estimate coverage days from reorder cycle or lead time if dates are missing
    if (coverageDays === 0 && (reorderCycle > 0 || vendorLeadTime > 0)) {
      coverageDays = reorderCycle > 0 ? reorderCycle : vendorLeadTime;
    }

    // Safely parse unitCost as a number
    const rawUnitCost = product['[average_unit_cost]'] || product.unit_cost || 0;
    const unitCost = this.parseNumber(rawUnitCost);

    // Map fields with fallbacks for different API response formats
    return {
      variant_id: product.variant_id || product['[Variant ID]'],
      productName: product.product || product['Item[Item Description]'],
      variantName: product.variant || product['Variant[Variant Name]'] || 'Default',
      sku: product.product_sku || product['product[SKU]'],
      image: product.image_url || product['[image_url]'],
      currentInventory,
      reorderQuantity: inventoryReplenishment,
      reorderDate,
      inventoryReplenishment,
      coverageDateRange,
      coverageDays,
      supplier_id: product.supplier_id || product['supplier_id'],
      supplier: product.supplier || product['supplier[name]'],
      unitCost,
      supplierSku: product.supplier_sku || product['supplier_sku'],
      // Additional fields for agent context sync (explain_recommendation)
      avgDailySales: this.parseNumber(product['[average_daily_units_sold_param]']),
      forecastDailySales: this.parseNumber(product['[average_daily_units_sold_forecast]']),
      vendorLeadTime,
      stockDays: this.parseNumber(product['[stock_days_inventory_planning]']),
      incomingInventory: this.parseNumber(product['[incoming_inventory_quantity]']),
      reorderCycle,
      safetyStock: this.parseNumber(product['[safety_stock_level]']),
      reorderPoint: this.parseNumber(product['[reorder_point]']),
      forecastLeadTimeDemand: this.parseNumber(product['[final_forecast_sales_current_cycle]']),
      forecastNextCycleDemand: this.parseNumber(product['[final_forecast_sales_next_cycle]']),
      // Daily velocity for coverage period (next cycle)
      forecastDailySalesCoverage: this.parseNumber(product['[average_daily_units_sold_forecast_next_cycle]']),
      // Filter-related fields for dynamic columns
      soldQuantity: this.parseNumber(product['[sold_quantity_param]']),
      grossProfit: this.parseNumber(product['[gross_profit]']),
      profitMargin: this.parseNumber(product['[product_profit_margin]']),
      productGrade: product['[product_grade]'] || ''
    };
  }

  private handleOptionsUpdate(options: any) {
    
    // Check if pagination actually changed before fetching
    const pageChanged = this.pageOptions.page !== options.page;
    const itemsPerPageChanged = this.pageOptions.itemsPerPage !== options.itemsPerPage;
    
    if (pageChanged || itemsPerPageChanged) {
      this.pageOptions.page = options.page;
      this.pageOptions.itemsPerPage = options.itemsPerPage;
      this.fetchProducts();
    }
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }

  private getSuggestedQtyColor(qty: number): string {
    if (qty === 0) return 'grey';
    if (qty < 10) return 'orange';
    if (qty < 50) return 'blue';
    return 'green';
  }

  /** Get color for current stock level indicator */
  private getStockLevelColor(qty: number): string {
    if (qty === 0) return 'error';      // Red - out of stock
    if (qty < 10) return 'warning';     // Orange - low stock
    return 'grey';                       // Grey - adequate stock
  }

  /** Format number with thousands separator */
  private formatNumber(value: number | undefined): string {
    if (value === undefined || value === null) return '0';
    return Math.round(value).toLocaleString();
  }

  /** Format decimal with 1 decimal place */
  private formatDecimal(value: number | undefined): string {
    if (value === undefined || value === null) return '0.0';
    return value.toFixed(1);
  }

  /** Format currency value */
  private formatCurrency(value: number | undefined): string {
    if (value === undefined || value === null) return CurrencyHelper.formatCurrency(0);
    return CurrencyHelper.formatCurrency(value || 0);
  }

  /** Format percentage value */
  private formatPercent(value: number | undefined): string {
    if (value === undefined || value === null) return '0%';
    return value.toFixed(1) + '%';
  }

  /** Get color for profit margin indicator */
  private getMarginColor(margin: number | undefined): string {
    if (margin === undefined || margin === null || margin <= 0) return 'grey';
    if (margin < 20) return 'error';     // Red - low margin
    if (margin < 40) return 'warning';   // Orange - medium margin
    return 'success';                    // Green - high margin
  }

  /** Get color for stock days indicator */
  private getStockDaysColor(days: number | undefined): string {
    if (days === undefined || days === null || days <= 0) return 'error';
    if (days <= 7) return 'error';       // Red - critical
    if (days <= 14) return 'warning';    // Orange - low
    if (days <= 30) return 'info';       // Blue - adequate
    return 'grey';                       // Grey - high stock
  }

  /** Get color for ABC grade indicator */
  private getGradeColor(grade: string | undefined): string {
    if (!grade) return 'grey';
    switch (grade.toUpperCase()) {
      case 'A': return 'success';        // Green - top priority
      case 'B': return 'info';           // Blue - medium priority
      case 'C': return 'warning';        // Orange - low priority
      default: return 'grey';
    }
  }

  private handleImageError(item: Product) {
    // Track failed image to trigger v-else placeholder
    this.failedImages.add(item.variant_id);
    // Force reactivity update since Set mutations aren't reactive
    this.failedImages = new Set(this.failedImages);
  }

  /** Timeout for PO creation confirmation (30 seconds) */
  private static readonly CREATE_PO_TIMEOUT_MS = 60000;

  /**
   * Wait for PO creation confirmation from EventBus
   * Resolves when confirmation received, rejects on timeout
   */
  private waitForCreateConfirmation(): Promise<void> {
    return new Promise((resolve, reject) => {
      let timeoutId: number | null = null;

      const handler = (payload: { metadata?: { action?: string } }) => {
        if (payload?.metadata?.action === 'create_po_from_buying_table:confirmed') {
          if (timeoutId !== null) {
            window.clearTimeout(timeoutId);
          }
          EventBus.$off('po-builder:create-response', handler);
          resolve();
        }
      };

      EventBus.$on('po-builder:create-response', handler);

      timeoutId = window.setTimeout(() => {
        EventBus.$off('po-builder:create-response', handler);
        reject(new Error('PO creation confirmation timed out'));
      }, BuyingTableDisplay.CREATE_PO_TIMEOUT_MS);
    });
  }

  /**
   * Direct PO creation - detects suppliers from selected products, handles multi-supplier case,
   * and shows draft PO preview before actual creation.
   */
  private createPODirect() {
    if (this.selectedProducts.length === 0) {
      EventBus.$emit('show-snackbar', {
        message: 'Please select at least one product',
        color: 'warning'
      });
      return;
    }

    // Extract unique suppliers from selected products
    const supplierMap = new Map<number, { id: number; name: string; items: Product[] }>();

    for (const product of this.selectedProducts) {
      const supplierId = parseInt(product.supplier_id, 10);
      if (!supplierId || supplierId <= 0) continue;

      if (!supplierMap.has(supplierId)) {
        supplierMap.set(supplierId, {
          id: supplierId,
          name: product.supplier || 'Unknown Supplier',
          items: []
        });
      }
      supplierMap.get(supplierId)!.items.push(product);
    }

    // Handle case: no valid suppliers found
    if (supplierMap.size === 0) {
      EventBus.$emit('show-snackbar', {
        message: 'No valid supplier found for selected products',
        color: 'error'
      });
      return;
    }

    // Handle case: single supplier - proceed directly to draft preview
    if (supplierMap.size === 1) {
      const [supplier] = supplierMap.values();
      this.showDraftPOPreviewForSupplier(supplier.id, supplier.name, supplier.items);
      return;
    }

    // Handle case: multiple suppliers - show selection dialog
    this.detectedSuppliers = Array.from(supplierMap.values()).map(s => ({
      id: s.id,
      name: s.name,
      itemCount: s.items.length
    }));
    this.selectedSupplierForPO = null;
    this.showSupplierSelectionDialog = true;
  }

  /**
   * Handle supplier selection from the multi-supplier dialog.
   * Filters products to the selected supplier and shows draft preview.
   */
  private confirmSupplierForPO() {
    if (!this.selectedSupplierForPO) return;

    const { id, name } = this.selectedSupplierForPO;
    this.showSupplierSelectionDialog = false;

    // Filter selected products to only those from the chosen supplier
    const supplierProducts = this.selectedProducts.filter(
      p => parseInt(p.supplier_id, 10) === id
    );

    this.showDraftPOPreviewForSupplier(id, name, supplierProducts);
  }

  /**
   * Show draft PO preview for selected supplier and items.
   * Emits event to display the draft in PurchaseOrderDisplay.
   */
  private showDraftPOPreviewForSupplier(supplierId: number, supplierName: string, products: Product[]) {
    // Transform products to draft PO items format
    const items = products.map(p => ({
      variantId: p.variant_id,
      productName: p.productName,
      variantName: p.variantName || 'Default',
      sku: p.sku || '',
      supplierSku: p.supplierSku || '',
      orderedQuantity: this.parseNumber(p.reorderQuantity, 1),
      unitPrice: this.parseNumber(p.unitCost),
      totalPrice: this.parseNumber(p.reorderQuantity, 1) * this.parseNumber(p.unitCost),
      variantImage: p.image || ''
    }));

    // Build draft PO data
    const draftPOData = {
      id: null,
      poNumber: 'DRAFT',
      status: 'draft_preview',
      supplierId,
      supplierName,
      orderDate: new Date().toISOString(),
      expectedDeliveryDate: null,
      items,
      totalAmount: items.reduce((sum, item) => sum + item.totalPrice, 0),
      notes: null,
      createdAt: new Date().toISOString()
    };

    // Emit to show PurchaseOrderDisplay with draft data
    EventBus.$emit('po-builder:preview', {
      actions: [{
        action: 'DISPLAY_DRAFT_PO_PREVIEW',
        parameters: {
          draft_po: draftPOData
        }
      }],
      current_agent: 'Purchase Order Preview'
    });

    // Clear selection after showing preview
    this.selectedProducts = [];
  }

  /**
   * Handle location selection confirmation from dialog.
   * Creates the PO with the selected delivery location.
   */
  private async onLocationConfirmed(selection: LocationSelection) {
    // Validate supplierId before proceeding
    const parsedSupplierId = this.supplierId ? parseInt(this.supplierId, 10) : NaN;
    if (!Number.isFinite(parsedSupplierId) || parsedSupplierId <= 0) {
      EventBus.$emit('show-snackbar', {
        message: 'Invalid supplier ID',
        color: 'error'
      });
      return;
    }

    this.creating = true;

    try {
      // Prepare items in the format required by PurchaseOrderModel
      const items: IPurchaseOrderItem[] = this.selectedProducts.map(p => {
        const unitPrice = this.parseNumber(p.unitCost);
        const orderedQuantity = this.parseNumber(p.reorderQuantity, 1);

        return {
          variantId: parseInt(p.variant_id, 10),
          productName: p.productName,
          variant: p.variantName || 'Default',
          sku: p.sku || '',
          supplierSku: p.supplierSku || '',
          variantImage: p.image || '',
          orderedQuantity,
          receivedQuantity: 0,
          unitPrice,
          totalPrice: orderedQuantity * unitPrice,
          note: ''
        };
      });

      // Create PO using PurchaseOrderModel with locationId
      const poData = {
        supplierId: parsedSupplierId,
        supplierName: this.supplierName,
        status: 'draft' as const,
        orderDate: new Date().toISOString(),
        totalAmount: items.reduce((sum, item) => sum + item.totalPrice, 0),
        locationId: selection.locationId,
        items
      };

      const newPO = new PurchaseOrderModel(poData);
      await newPO.save();

      // Update context with the created PO
      if (contextSyncService.getSessionId()) {
        await contextSyncService.updateSelectionContext(
          'purchase_order',
          [newPO.id!],
          [{
            draft_po_id: newPO.id,
            po_number: newPO.poNumber,
            total_amount: newPO.totalAmount,
            supplier_id: parsedSupplierId,
            supplier_name: this.supplierName,
            location_id: selection.locationId,
            location_name: selection.locationName
          }]
        );
      }

      // Emit event to display the PO detail (ChatResponse format with actions array)
      EventBus.$emit('po-builder:preview', {
        actions: [{
          action: 'DISPLAY_PURCHASE_ORDER',
          parameters: {
            po_id: String(newPO.id),  // Use po_id for direct creation (not draft_po_id)
            po_number: newPO.poNumber
          }
        }],
        current_agent: 'Purchase Order Assistant'
      });

      // Clear selection after success
      this.selectedProducts = [];

      EventBus.$emit('show-snackbar', {
        message: `Purchase order ${newPO.poNumber} created successfully`,
        color: 'success'
      });

    } catch (error) {
      console.error('[BuyingTableDisplay] Failed to create PO:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to create purchase order',
        color: 'error'
      });
    } finally {
      this.creating = false;
    }
  }

  /**
   * Handle location dialog cancellation.
   */
  private onLocationCancelled() {
    // User cancelled - no action needed
  }

  /**
   * Request AI review of selected items before creating PO.
   * Agent analyzes quantities, supplier constraints, and provides recommendations.
   */
  private requestAIReview() {
    if (this.selectedProducts.length === 0) {
      EventBus.$emit('show-snackbar', {
        message: 'Please select at least one product',
        color: 'warning'
      });
      return;
    }

    this.reviewing = true;

    try {
      // Prepare items with full context for AI review
      const selectedItems = this.selectedProducts.map(p => {
        const unitPrice = this.parseNumber(p.unitCost);
        const orderedQuantity = this.parseNumber(p.reorderQuantity, 1);
        const suggestedQuantity = this.parseNumber(p.inventoryReplenishment);

        return {
          variantId: p.variant_id,
          productName: p.productName,
          variantName: p.variantName || 'Default',
          sku: p.sku,
          orderedQuantity,
          suggestedQuantity,
          unitPrice,
          currentInventory: this.parseNumber(p.currentInventory),
          coverageDays: this.parseNumber(p.coverageDays),
          reorderDate: p.reorderDate,
          // Additional context for review
          avgDailySales: this.parseNumber(p.avgDailySales),
          forecastDailySales: this.parseNumber(p.forecastDailySales),
          vendorLeadTime: this.parseNumber(p.vendorLeadTime, 14),
          incomingInventory: this.parseNumber(p.incomingInventory),
          safetyStock: this.parseNumber(p.safetyStock),
          // Order constraints
          caseQuantity: this.parseNumber(p.caseQuantity),
          minOrderQuantity: this.parseNumber(p.minOrderQuantity),
          maxOrderQuantity: this.parseNumber(p.maxOrderQuantity),
          // For PO creation after review
          supplierSku: p.supplierSku || '',
          variantImage: p.image || ''
        };
      });

      // Calculate totals for the message
      const totalUnits = selectedItems.reduce((sum, item) => sum + item.orderedQuantity, 0);
      const totalValue = selectedItems.reduce((sum, item) => sum + (item.orderedQuantity * item.unitPrice), 0);

      const message = `Please review my selection of ${this.selectedProducts.length} products for purchase order. Total: ${totalUnits} units, $${totalValue.toFixed(2)}`;

      // Get effective supplier info (from dropdown filter or actionParameters)
      const effectiveSupplierId = this.selectedSupplierFilter?.id?.toString() || this.supplierId || '';
      const effectiveSupplierName = this.selectedSupplierFilter?.name || this.supplierName || '';

      EventBus.$emit('chatbox:send-message', {
        message,
        metadata: {
          action: 'review_po_items',
          context: {
            selection: {
              selection_type: 'buying_table_items',
              selected_items: selectedItems,
              // Include supplier info so agent knows which supplier context we're in
              supplier_id: effectiveSupplierId,
              supplier_name: effectiveSupplierName,
              summary: {
                totalItems: this.selectedProducts.length,
                totalUnits,
                totalValue,
                estimatedCoverageDays: this.estimatedCoverageDays
              }
            }
          }
        }
      });

    } finally {
      this.reviewing = false;
    }
  }

  /**
   * Request explanation of suggestions for selected items.
   * Agent explains why we recommend ordering the suggested quantities.
   */
  private requestExplainSuggestion() {
    if (this.selectedProducts.length === 0) {
      EventBus.$emit('show-snackbar', {
        message: 'Please select at least one product',
        color: 'warning'
      });
      return;
    }

    this.explaining = true;

    try {
      // Prepare items with full context for explanation
      const selectedItems = this.selectedProducts.map(p => ({
        variantId: p.variant_id,
        productName: p.productName,
        variantName: p.variantName || 'Default',
        sku: p.sku,
        // Inventory metrics
        currentInventory: this.parseNumber(p.currentInventory),
        suggestedQuantity: this.parseNumber(p.inventoryReplenishment),
        reorderQuantity: this.parseNumber(p.reorderQuantity),
        // Sales velocity
        avgDailySales: this.parseNumber(p.avgDailySales),
        forecastDailySales: this.parseNumber(p.forecastDailySales),
        // Timing and coverage
        reorderDate: p.reorderDate,
        coverageDays: this.parseNumber(p.coverageDays),
        coverageStartDate: p.coverageStartDate,
        coverageEndDate: p.coverageEndDate,
        stockDays: this.parseNumber(p.stockDays),
        inventoryRemainingDays: this.parseNumber(p.inventoryRemainingDays),
        // Supply chain
        vendorLeadTime: this.parseNumber(p.vendorLeadTime, 14),
        incomingInventory: this.parseNumber(p.incomingInventory),
        // Safety and constraints
        safetyStock: this.parseNumber(p.safetyStock),
        reorderCycle: this.parseNumber(p.reorderCycle),
        reorderPoint: this.parseNumber(p.reorderPoint),
        // Order constraints
        caseQuantity: this.parseNumber(p.caseQuantity),
        minOrderQuantity: this.parseNumber(p.minOrderQuantity),
        maxOrderQuantity: this.parseNumber(p.maxOrderQuantity),
        // Forecast
        forecastLeadTimeDemand: this.parseNumber(p.forecastLeadTimeDemand),
        forecastNextCycleDemand: this.parseNumber(p.forecastNextCycleDemand),
        forecastInventoryNeed: this.parseNumber(p.forecastInventoryNeed),
        // Cost
        unitCost: this.parseNumber(p.unitCost)
      }));

      // Build a descriptive message
      const productIdentifiers = this.selectedProducts.slice(0, 3).map(p =>
        p.sku ? `SKU (${p.sku})` : `Product: ${p.productName} (variant ${p.variant_id})`
      );
      const productList = productIdentifiers.join(', ') + (this.selectedProducts.length > 3 ? ` and ${this.selectedProducts.length - 3} more` : '');
      const message = `Please explain why you suggest ordering these quantities for ${productList}. What factors went into the recommendation?`;

      // Get effective supplier info (from dropdown filter or actionParameters)
      const effectiveSupplierId = this.selectedSupplierFilter?.id?.toString() || this.supplierId || '';
      const effectiveSupplierName = this.selectedSupplierFilter?.name || this.supplierName || '';

      EventBus.$emit('chatbox:send-message', {
        message,
        metadata: {
          action: 'explain_suggestion',
          context: {
            selection: {
              selection_type: 'buying_table_items',
              selected_items: selectedItems,
              // Include supplier info so agent knows which supplier context we're in
              supplier_id: effectiveSupplierId,
              supplier_name: effectiveSupplierName,
              // Include date range for historical sales context
              date_range: this.currentDateRange,
              date_range_display: this.dateRangeDisplay,
              summary: {
                totalItems: this.selectedProducts.length,
                totalUnits: this.totalUnits,
                totalValue: this.totalCost,
                estimatedCoverageDays: this.estimatedCoverageDays
              }
            }
          }
        }
      });

    } finally {
      this.explaining = false;
    }
  }
}
